// Pure A1-notation helpers (no Office.js). Used by the executor for shape checks and by the
// UI for labels. Tool inputs always carry `sheet` + a bare A1 `address` (#14).

export interface CellRef {
  row: number; // 1-based
  col: number; // 1-based
}

export interface RangeRef {
  start: CellRef;
  end: CellRef;
}

const CELL_RE = /^\$?([A-Za-z]{1,3})\$?(\d{1,7})$/;
const COL_RE = /^\$?([A-Za-z]{1,3})$/;
const ROW_RE = /^\$?(\d{1,7})$/;

/** 1-based column index → letters ('A', 'Z', 'AA', …). */
export function colToLetter(col: number): string {
  if (!Number.isInteger(col) || col < 1) throw new Error(`Invalid column index: ${col}`);
  let s = '';
  let n = col;
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/** Column letters → 1-based index ('A' → 1, 'AA' → 27). */
export function letterToCol(letters: string): number {
  const up = letters.toUpperCase();
  if (!/^[A-Z]{1,3}$/.test(up)) throw new Error(`Invalid column letters: ${letters}`);
  let n = 0;
  for (const ch of up) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

/** Strip an optional `Sheet!` prefix (tolerated on input, never required). */
export function stripSheet(address: string): string {
  const bang = address.lastIndexOf('!');
  return bang >= 0 ? address.slice(bang + 1) : address;
}

export function parseCell(cell: string): CellRef {
  const m = CELL_RE.exec(stripSheet(cell).trim());
  if (!m) throw new Error(`Invalid cell address: ${cell}`);
  return { col: letterToCol(m[1]), row: parseInt(m[2], 10) };
}

/** Parse 'B2', 'B2:D5', 'B:D' (whole columns) or '3:5' (whole rows). Whole rows/columns are
 *  bounded to Excel's limits so callers can still compute a shape. */
export function parseRange(address: string): RangeRef {
  const a = stripSheet(address).trim();
  if (!a) throw new Error('Empty address');
  const [left, right] = a.split(':');
  if (right === undefined) {
    const c = parseCell(left);
    return { start: c, end: { ...c } };
  }
  const lc = COL_RE.exec(left);
  const rc = COL_RE.exec(right);
  if (lc && rc) {
    return { start: { col: letterToCol(lc[1]), row: 1 }, end: { col: letterToCol(rc[1]), row: 1_048_576 } };
  }
  const lr = ROW_RE.exec(left);
  const rr = ROW_RE.exec(right);
  if (lr && rr) {
    return { start: { col: 1, row: parseInt(lr[1], 10) }, end: { col: 16_384, row: parseInt(rr[1], 10) } };
  }
  const s = parseCell(left);
  const e = parseCell(right);
  return {
    start: { row: Math.min(s.row, e.row), col: Math.min(s.col, e.col) },
    end: { row: Math.max(s.row, e.row), col: Math.max(s.col, e.col) },
  };
}

export function isValidAddress(address: string): boolean {
  try {
    parseRange(address);
    return true;
  } catch {
    return false;
  }
}

export function rangeToA1(r: RangeRef): string {
  const s = `${colToLetter(r.start.col)}${r.start.row}`;
  const e = `${colToLetter(r.end.col)}${r.end.row}`;
  return s === e ? s : `${s}:${e}`;
}

export function rangeShape(address: string): { rows: number; cols: number } {
  const r = parseRange(address);
  return { rows: r.end.row - r.start.row + 1, cols: r.end.col - r.start.col + 1 };
}

export function cellCount(address: string): number {
  const { rows, cols } = rangeShape(address);
  return rows * cols;
}

/** True when a 2-D array has exactly `rows × cols` (rectangular). */
export function matrixShape(values: unknown[][]): { rows: number; cols: number } | null {
  if (!Array.isArray(values) || values.length === 0) return null;
  const cols = Array.isArray(values[0]) ? values[0].length : -1;
  if (cols < 1) return null;
  for (const row of values) if (!Array.isArray(row) || row.length !== cols) return null;
  return { rows: values.length, cols };
}

/** Validate that `values` fits `address` exactly; returns an error message or null. */
export function checkShape(address: string, values: unknown[][]): string | null {
  const m = matrixShape(values);
  if (!m) return 'values must be a non-empty rectangular 2-D array';
  const s = rangeShape(address);
  if (m.rows !== s.rows || m.cols !== s.cols) {
    return `values shape ${m.rows}x${m.cols} does not match ${address} (${s.rows}x${s.cols})`;
  }
  return null;
}

/** Address of the top-left cell offset by (rowOffset, colOffset) — for journal/undo bookkeeping. */
export function offsetCell(cell: string, rowOffset: number, colOffset: number): string {
  const c = parseCell(cell);
  return `${colToLetter(c.col + colOffset)}${c.row + rowOffset}`;
}

/** Sheet!address label for cards and previews (quotes sheet names with spaces). */
export function qualify(sheet: string, address: string): string {
  const needsQuote = /[^A-Za-z0-9_]/.test(sheet);
  return `${needsQuote ? `'${sheet.replace(/'/g, "''")}'` : sheet}!${address}`;
}

/** Parse a "1:3" / "3" row spec or "B:D" / "B" column spec into an A1 range for whole rows/cols. */
export function wholeRowsAddress(rows: string): string {
  const [a, b = a] = rows.split(':').map((s) => s.trim());
  if (!ROW_RE.test(a) || !ROW_RE.test(b)) throw new Error(`Invalid rows spec: ${rows}`);
  return `${a}:${b}`;
}

export function wholeColumnsAddress(columns: string): string {
  const [a, b = a] = columns.split(':').map((s) => s.trim().toUpperCase());
  if (!COL_RE.test(a) || !COL_RE.test(b)) throw new Error(`Invalid columns spec: ${columns}`);
  return `${a}:${b}`;
}

/** Full destination range of a copy: `dest` anchor + the source shape (swapped when transposed).
 *  Shared by the executor (`_changes`) and the runner (journal before-image) so both agree. */
export function copyDestination(source: string, dest: string, transpose = false): string {
  const shape = rangeShape(source);
  const rows = transpose ? shape.cols : shape.rows;
  const cols = transpose ? shape.rows : shape.cols;
  const start = parseRange(dest).start;
  const startCell = `${colToLetter(start.col)}${start.row}`;
  const endCell = offsetCell(startCell, rows - 1, cols - 1);
  return startCell === endCell ? startCell : `${startCell}:${endCell}`;
}

/** Column letter + row number that is NOT an R1C1 token (R10 / C7 are excluded). */
const A1_REF_IN_R1C1 = /(?<![A-Z])(?![RC]\d)[A-Z]{1,3}\$?\d+(?![\dC[])/;
/** A whole R1C1 reference — `R7C3`, `RC9`, `R[-1]C`, `RC[-1]`, `R5C` — removed before looking for A1 addresses:
 *  `RC9` (same row, column 9) otherwise reads as column "RC", row 9, and a valid formula is refused. Not inside a
 *  word (`ROUND`, `SOURCE`): letters around it keep it. */
const R1C1_REF = /(?<![A-Z_])R(?:\[-?\d+\]|\d+)?C(?:\[-?\d+\]|\d+)?(?![A-Z_(])/g;

/**
 * Why `formula` is not a valid R1C1 pattern (null when it looks fine). Mirrors the backend validator:
 * the model's most frequent mistake is mixing A1 absolute references ($C$7) into an R1C1 string, which
 * Excel rejects with a bare GeneralException — this turns it into an actionable error before the call.
 */
export function r1c1Problem(formula: string): string | null {
  if (!formula.startsWith('=')) return "formula_r1c1 must start with '='";
  // Strip quoted strings and sheet names before inspecting (e.g. "$" literals, 'Inputs'!R4C2).
  const stripped = formula.replace(/"[^"]*"|'[^']*'/g, '');
  if (stripped.includes('$')) {
    return "formula_r1c1 must be pure R1C1 and never contains '$': write absolute references as R7C3 (row 7, column C), mixed as R7C / RC3, relative as R[-1]C / RC[-1]. To use A1 references such as $C$7, pass `formulas` (2-D array of A1 formulas) instead.";
  }
  if (A1_REF_IN_R1C1.test(stripped.replace(R1C1_REF, ' '))) {
    return 'formula_r1c1 seems to contain an A1 reference (column letter + row number). R1C1 uses R<row>C<col> with numbers only (e.g. R7C3, RC[-1]). Use `formulas` for A1-style formulas.';
  }
  return null;
}

/** Max areas accepted in one multi-area address ("D13,D42,D43"). */
export const MAX_AREAS = 20;

/** Split a possibly multi-area A1 address into its normalised areas (each validated with the
 *  single-area rules; no sheet prefix allowed). "B2" → ["B2"]; "B4:F4, B6:F6" → ["B4:F4","B6:F6"]. */
export function parseAreas(address: string): string[] {
  const parts = String(address ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (!parts.length) throw new Error('Empty address');
  if (parts.length > MAX_AREAS) throw new Error(`Too many areas (${parts.length} > ${MAX_AREAS})`);
  return parts.map((p) => {
    if (p.includes('!')) throw new Error(`address must not contain a sheet prefix: ${p}`);
    return rangeToA1(parseRange(p));
  });
}

/** True when the address holds more than one area. */
export function isMultiArea(address: string): boolean {
  return String(address ?? '').includes(',');
}

/** Total cell count across the areas of a (possibly multi-area) address. */
export function areasCellCount(areas: string[]): number {
  return areas.reduce((n, a) => n + cellCount(a), 0);
}

/** Smallest rectangle containing every given address ("B7", "D3", "F9:F12" → "B3:F12"). Scattered
 *  targets are highlighted and journalled through their bounding box: a multi-area address only holds
 *  MAX_AREAS areas, and one rectangle is one snapshot instead of one per cell. Throws on an empty list
 *  or an unparseable address. */
export function boundingBox(addresses: string[]): string {
  if (!addresses.length) throw new Error('Empty address list');
  let top = Number.POSITIVE_INFINITY;
  let left = Number.POSITIVE_INFINITY;
  let bottom = 0;
  let right = 0;
  for (const a of addresses) {
    const r = parseRange(a);
    top = Math.min(top, r.start.row);
    left = Math.min(left, r.start.col);
    bottom = Math.max(bottom, r.end.row);
    right = Math.max(right, r.end.col);
  }
  return rangeToA1({ start: { row: top, col: left }, end: { row: bottom, col: right } });
}

/** Split "Outputs!B3:F4" into { sheet: "Outputs", address: "B3:F4" } (quotes stripped); sheet is null
 *  when the address carries no prefix. */
export function splitSheetPrefix(address: string): { sheet: string | null; address: string } {
  const a = String(address ?? '').trim();
  const bang = a.lastIndexOf('!');
  if (bang < 0) return { sheet: null, address: a };
  let sheet = a.slice(0, bang).trim();
  if (sheet.startsWith("'") && sheet.endsWith("'")) sheet = sheet.slice(1, -1).replace(/''/g, "'");
  return { sheet: sheet || null, address: a.slice(bang + 1).trim() };
}
